import { useCallback, useEffect, useReducer, useState } from 'react';
import { createGameState, DEFAULT_SETTINGS, gameReducer, GameAction, GameState, remainingMs } from '../utils/initialsGame';

/** Restores a saved game, falling back to a fresh one on missing or malformed data. */
const loadState = (storageKey: string): GameState => {
  const fresh = createGameState();
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) ?? 'null');
    if (!saved || typeof saved !== 'object' || !saved.settings || !Array.isArray(saved.rounds)) return fresh;
    const state: GameState = {
      ...fresh,
      ...saved,
      // Settings added later get their defaults on games saved before them.
      settings: { ...DEFAULT_SETTINGS, ...saved.settings },
      past: Array.isArray(saved.past) ? saved.past : [],
    };
    // A turn that was running when the page closed comes back paused.
    return state.timer?.endsAt ? { ...state, timer: { endsAt: null, remainingMs: remainingMs(state.timer, Date.now()) } } : state;
  } catch {
    return fresh;
  }
};

/**
 * React binding for utils/initialsGame.ts. Keeps the game in this browser
 * (survives reloads) and ends a turn by itself when its time runs out.
 */
export const useInitialsGame = (storageKey: string) => {
  const [state, rawDispatch] = useReducer(gameReducer, storageKey, loadState);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // storage unavailable (private mode) — the game just won't survive a reload
    }
  }, [state, storageKey]);

  const running = state.phase === 'turn' && state.timer.endsAt !== null;

  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => setNow(Date.now()), 200);
    return () => window.clearInterval(id);
  }, [running]);

  const left = remainingMs(state.timer, now);
  const roundNo = state.rounds.length;

  useEffect(() => {
    if (running && left <= 0) {
      rawDispatch({ type: 'fail', side: state.turn, round: roundNo, reason: 'timeout', now: Date.now() });
    }
  }, [running, left, state.turn, roundNo]);

  const dispatch = useCallback((action: GameAction) => {
    setNow(Date.now());
    rawDispatch(action);
  }, []);

  return { state, dispatch, remainingMs: left, running };
};
